import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Search, ChevronRight, ChevronDown, ArrowUpDown, Download, FileText, FileSpreadsheet, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function MovimentiStorico() {
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDeposito, setFilterDeposito] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [sortBy, setSortBy] = useState("ultimo");
  const [sortDir, setSortDir] = useState("desc");
  const [downloadBusy, setDownloadBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, p, d] = await Promise.all([
          api.get("/movements?limit=20000"),
          api.get("/products"),
          api.get("/deposits"),
        ]);
        setMovements(m.data || []);
        setProducts(p.data || []);
        setDeposits(d.data || []);
      } catch (e) {
        toast.error("Errore caricamento storico movimenti");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const depositName = (id) => deposits.find((d) => d.id === id)?.nome || "—";

  // Movement involves the selected deposit if its primary deposit OR transfer endpoints match.
  const movementInDeposit = (m, depId) => {
    if (!depId || depId === "all") return true;
    return m.deposito_id === depId || m.from_deposito_id === depId || m.to_deposito_id === depId;
  };

  const movementInDateRange = (m) => {
    if (!dateFrom && !dateTo) return true;
    const t = m.created_at || "";
    if (dateFrom && t < dateFrom) return false;
    if (dateTo && t > dateTo + "T23:59:59.999999") return false;
    return true;
  };

  // Filter movements by selected warehouse + date range before grouping
  const filteredMovements = useMemo(
    () => movements.filter((m) => movementInDeposit(m, filterDeposito) && movementInDateRange(m)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [movements, filterDeposito, dateFrom, dateTo]
  );

  // Group movements by prodotto_id (multi-deposit duplicates share name+supplier — but movements
  // reference a specific product instance, so we group by prodotto_id then merge same-name groups)
  const groups = useMemo(() => {
    const byProduct = new Map();
    for (const m of filteredMovements) {
      const pid = m.prodotto_id;
      if (!pid) continue;
      const product = products.find((p) => p.id === pid);
      // Group key: nome_articolo + fornitore_id (to merge multi-deposit duplicates)
      const nameKey = (product?.nome_articolo || pid).trim().toLowerCase();
      const key = `${nameKey}::${product?.fornitore_id || ""}`;
      if (!byProduct.has(key)) {
        byProduct.set(key, {
          key,
          prodotto_id: pid,
          nome: product?.nome_articolo || "(prodotto eliminato)",
          movimenti: [],
          entrate: 0,
          uscite: 0,
          ultimo: null,
        });
      }
      const g = byProduct.get(key);
      g.movimenti.push(m);
      const qty = Number(m.quantita) || 0;
      if (m.tipo === "carico") g.entrate += qty;
      else g.uscite += qty;
      if (!g.ultimo || m.created_at > g.ultimo) g.ultimo = m.created_at;
    }
    return Array.from(byProduct.values()).map((g) => {
      // Sort movements chronologically (newest first)
      g.movimenti.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      g.netto = g.entrate - g.uscite;
      g.count = g.movimenti.length;
      return g;
    });
  }, [filteredMovements, products]);

  const filteredSorted = useMemo(() => {
    let arr = groups;
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((g) => g.nome.toLowerCase().includes(q));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const keyOf = (g) => {
      switch (sortBy) {
        case "nome": return g.nome.toLowerCase();
        case "entrate": return g.entrate;
        case "uscite": return g.uscite;
        case "netto": return g.netto;
        case "count": return g.count;
        case "ultimo": return g.ultimo || "";
        default: return g.nome;
      }
    };
    return [...arr].sort((a, b) => {
      const va = keyOf(a), vb = keyOf(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [groups, search, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir(col === "nome" ? "asc" : "desc"); }
  };

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(filteredSorted.map((g) => g.key)));
  const collapseAll = () => setExpanded(new Set());

  const downloadReport = async (format) => {
    setDownloadBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterDeposito && filterDeposito !== "all") params.set("deposito_id", filterDeposito);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const token = localStorage.getItem("ds_token");
      const url = `${API}/movements-history/${format}?${params.toString()}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      // Try to read filename from response headers
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `storico_movimenti.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      toast.success(`File ${format.toUpperCase()} scaricato`);
    } catch (e) {
      toast.error(`Errore download: ${e.message || e}`);
    } finally {
      setDownloadBusy(false);
    }
  };

  const totalsAll = useMemo(() => {
    return filteredSorted.reduce((acc, g) => {
      acc.entrate += g.entrate;
      acc.uscite += g.uscite;
      acc.count += g.count;
      return acc;
    }, { entrate: 0, uscite: 0, count: 0 });
  }, [filteredSorted]);

  if (loading) {
    return <div className="font-brand text-2xl" style={{ color: "var(--ds-primary)" }}>Caricamento storico…</div>;
  }

  const Th = ({ col, children, align = "left" }) => (
    <th
      onClick={() => toggleSort(col)}
      className={`label-tag py-2 px-2 cursor-pointer select-none ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"}`}
      data-testid={`sort-${col}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown size={11} style={{ opacity: sortBy === col ? 1 : 0.3 }} />
      </span>
    </th>
  );

  return (
    <div className="space-y-6" data-testid="storico-movimenti-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="label-tag">Operazioni</p>
          <h1 className="font-brand text-4xl sm:text-5xl font-light mt-1">Storico Movimenti</h1>
          <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
            Tutte le movimentazioni raggruppate per prodotto. Espandi una riga per vedere la cronologia dettagliata.
          </p>
        </div>
        <div className="ds-card px-5 py-3">
          <div className="label-tag mb-1">
            {filterDeposito === "all" ? "Totale (tutti i magazzini)" : `Totale: ${depositName(filterDeposito)}`}
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="label-tag">Entrate</div>
              <div className="font-medium" style={{ color: "#7B8B6F" }} data-testid="totale-entrate">
                +{totalsAll.entrate}
              </div>
            </div>
            <div>
              <div className="label-tag">Uscite</div>
              <div className="font-medium" style={{ color: "#8B3A36" }} data-testid="totale-uscite">
                −{totalsAll.uscite}
              </div>
            </div>
            <div>
              <div className="label-tag">Movimenti</div>
              <div className="font-medium" data-testid="totale-movimenti">{totalsAll.count}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="ds-card p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-text-secondary)" }} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca prodotto…"
              className="pl-9 rounded-lg"
              data-testid="storico-search"
            />
          </div>
          <div className="min-w-[220px]">
            <Select value={filterDeposito} onValueChange={setFilterDeposito}>
              <SelectTrigger className="rounded-lg" data-testid="storico-filter-deposito">
                <SelectValue placeholder="Filtra magazzino…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i magazzini</SelectItem>
                {[...deposits].sort((a, b) => a.nome.localeCompare(b.nome, "it")).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-xs px-3 py-2 rounded-lg border inline-flex items-center gap-1.5 transition-colors hover:bg-[var(--ds-bg)]"
                style={{ borderColor: "var(--ds-border)", color: "var(--ds-text-primary)" }}
                data-testid="storico-date-trigger"
              >
                <Calendar size={14} />
                {dateFrom || dateTo
                  ? `${dateFrom || "—"} → ${dateTo || "—"}`
                  : "Periodo"}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="label-tag">Da</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="mt-1 rounded-lg text-sm"
                      data-testid="storico-date-from"
                    />
                  </div>
                  <div>
                    <Label className="label-tag">A</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="mt-1 rounded-lg text-sm"
                      data-testid="storico-date-to"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="w-full text-xs py-1.5 rounded-lg border"
                  style={{ borderColor: "var(--ds-border)", color: "var(--ds-text-secondary)" }}
                  data-testid="storico-date-reset"
                >
                  Azzera periodo
                </button>
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={downloadBusy}
                className="text-xs px-3 py-2 rounded-lg inline-flex items-center gap-1.5 transition-colors hover:opacity-90"
                style={{ background: "var(--ds-primary)", color: "white" }}
                data-testid="storico-download-trigger"
              >
                <Download size={14} />
                {downloadBusy ? "Scaricamento…" : "Esporta"}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2">
              <button
                type="button"
                onClick={() => downloadReport("pdf")}
                disabled={downloadBusy}
                className="w-full text-left text-sm px-3 py-2 rounded-md hover:bg-[var(--ds-bg)] inline-flex items-center gap-2"
                data-testid="storico-download-pdf"
              >
                <FileText size={14} /> Scarica PDF
              </button>
              <button
                type="button"
                onClick={() => downloadReport("xlsx")}
                disabled={downloadBusy}
                className="w-full text-left text-sm px-3 py-2 rounded-md hover:bg-[var(--ds-bg)] inline-flex items-center gap-2"
                data-testid="storico-download-xlsx"
              >
                <FileSpreadsheet size={14} /> Scarica Excel (XLSX)
              </button>
            </PopoverContent>
          </Popover>
          <button
            type="button"
            onClick={expandAll}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors hover:bg-[var(--ds-bg)]"
            style={{ borderColor: "var(--ds-border)", color: "var(--ds-text-primary)" }}
            data-testid="expand-all"
          >
            Espandi tutto
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="text-xs px-3 py-1.5 rounded-full border transition-colors hover:bg-[var(--ds-bg)]"
            style={{ borderColor: "var(--ds-border)", color: "var(--ds-text-primary)" }}
            data-testid="collapse-all"
          >
            Comprimi tutto
          </button>
          <div className="text-sm ml-auto" style={{ color: "var(--ds-text-secondary)" }}>
            {filteredSorted.length} prodotti
          </div>
        </div>

        {filteredSorted.length === 0 ? (
          <div className="text-sm py-10 text-center" style={{ color: "var(--ds-text-secondary)" }}>
            {search ? "Nessun prodotto trovato." : "Nessun movimento registrato."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--ds-border)" }}>
                  <th className="w-8"></th>
                  <Th col="nome">Prodotto</Th>
                  <Th col="count" align="center">Movimenti</Th>
                  <Th col="entrate" align="right">Entrate</Th>
                  <Th col="uscite" align="right">Uscite</Th>
                  <Th col="netto" align="right">Netto</Th>
                  <Th col="ultimo" align="right">Ultimo movimento</Th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.map((g) => {
                  const isOpen = expanded.has(g.key);
                  const multi = g.movimenti.length > 1;
                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        className="border-b cursor-pointer hover:bg-[var(--ds-bg)]"
                        style={{ borderColor: "var(--ds-border)" }}
                        onClick={() => toggleExpand(g.key)}
                        data-testid={`storico-row-${g.prodotto_id}`}
                      >
                        <td className="py-3 px-2">
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors"
                            style={{ background: isOpen ? "var(--ds-primary)" : "var(--ds-bg)", color: isOpen ? "#fff" : "var(--ds-text-primary)" }}
                            data-testid={`expand-${g.prodotto_id}`}
                          >
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </span>
                        </td>
                        <td className="py-3 px-2 font-medium">
                          <Link
                            to={`/prodotti/${g.prodotto_id}`}
                            className="hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: "var(--ds-text-primary)" }}
                          >
                            {g.nome}
                          </Link>
                        </td>
                        <td className="py-3 px-2 text-center text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                          {g.count}{multi && <span className="ml-1 text-[10px] opacity-70">(+)</span>}
                        </td>
                        <td className="py-3 px-2 text-right font-medium" style={{ color: "#7B8B6F" }}>
                          +{g.entrate}
                        </td>
                        <td className="py-3 px-2 text-right font-medium" style={{ color: "#8B3A36" }}>
                          −{g.uscite}
                        </td>
                        <td className="py-3 px-2 text-right font-medium">
                          {g.netto >= 0 ? `+${g.netto}` : g.netto}
                        </td>
                        <td className="py-3 px-2 text-right text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                          {g.ultimo ? new Date(g.ultimo).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ background: "var(--ds-bg)" }}>
                          <td colSpan={7} className="px-4 pb-4 pt-2">
                            <div className="rounded-lg overflow-hidden border" style={{ borderColor: "var(--ds-border)", background: "var(--ds-surface)" }}>
                              <table className="w-full text-sm">
                                <thead style={{ background: "var(--ds-bg)" }}>
                                  <tr style={{ borderBottom: "1px solid var(--ds-border)" }}>
                                    <th className="text-left py-2 px-3 label-tag">Data</th>
                                    <th className="text-left py-2 px-3 label-tag">Tipo</th>
                                    <th className="text-left py-2 px-3 label-tag">Deposito</th>
                                    <th className="text-right py-2 px-3 label-tag">Q.tà</th>
                                    <th className="text-left py-2 px-3 label-tag">Utente</th>
                                    <th className="text-left py-2 px-3 label-tag">Note</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {g.movimenti.map((m) => {
                                    const depName = depositName(m.deposito_id || m.to_deposito_id);
                                    const fromName = m.from_deposito_id ? depositName(m.from_deposito_id) : null;
                                    const isCarico = m.tipo === "carico";
                                    return (
                                      <tr key={m.id} style={{ borderBottom: "1px solid var(--ds-border)" }}>
                                        <td className="py-2 px-3 text-xs">{new Date(m.created_at).toLocaleString("it-IT")}</td>
                                        <td className="py-2 px-3">
                                          <span className="text-xs font-medium" style={{ color: isCarico ? "#7B8B6F" : "#8B3A36" }}>
                                            {isCarico ? "carico" : "scarico"}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 text-xs">
                                          {fromName ? `${fromName} → ${depName}` : depName}
                                        </td>
                                        <td className="py-2 px-3 text-right font-medium">
                                          <span style={{ color: isCarico ? "#7B8B6F" : "#8B3A36" }}>
                                            {isCarico ? "+" : "−"}{m.quantita}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 text-xs">{m.utente_nome || "—"}</td>
                                        <td className="py-2 px-3 text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                                          {m.note || "—"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
